import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Form, Input, Select, Space, Spin, Switch, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { FilePenLine, Save } from 'lucide-react'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { EMPTY_CONTENT } from '@/features/contract/const/empty-content.ts'
import { NEW_TEMPLATE_CONTENT, NEW_TEMPLATE_INITIAL_VALUES } from '@/features/contract/const/new-template.ts'
import type { FormValues, TemplateType } from '@/features/contract/types/template.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CONTRACT_TEMPLATE_TYPES, CONTRACT_TEMPLATE_VERSION_STATUS } from './contract-labels.ts'
import { ContractLexicalEditor, type ContractLexicalContent } from './contract-lexical-editor.tsx'

export type ContractTemplateRecord = {
  id: number
  code: string
  name: string
  type: TemplateType
  description: string | null
  isEnabled: boolean
  currentVersion: number
  latestVersion: { id: number; version: number; status: 'draft' | 'published' | 'archived' } | null
}

export type ContractTemplateListSearch = {
  current: number
  pageSize: number
  keyword?: string
  type?: TemplateType
  status?: 'enabled' | 'disabled'
}

function newTemplateCode() {
  return `contract-${dayjs().valueOf().toString(36)}`
}

export function ContractTemplateEditorPage({
  canManage,
  returnSearch,
  templateId,
  versionId,
  readOnly = false
}: {
  canManage: boolean
  returnSearch: ContractTemplateListSearch
  templateId?: number
  versionId?: number
  readOnly?: boolean
}) {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [content, setContent] = useState<ContractLexicalContent>(() =>
    templateId || versionId ? EMPTY_CONTENT : NEW_TEMPLATE_CONTENT
  )
  const [loadedContentKey, setLoadedContentKey] = useState<string>()
  const hydratedTemplateKey = useRef<string | undefined>(undefined)
  const [generatedCode] = useState(() => newTemplateCode())
  const isVersionView = Boolean(versionId)
  const isReadOnly = readOnly || !canManage
  const selectedVersion = useQuery({
    ...trpc.contract.template.versionDetail.queryOptions({ id: versionId ?? 0 }),
    enabled: isVersionView
  })
  const resolvedTemplateId = templateId ?? selectedVersion.data?.templateId
  const templateKey = resolvedTemplateId ? `template-${resolvedTemplateId}` : versionId ? `version-${versionId}` : 'new'
  const detail = useQuery({
    ...trpc.contract.template.detail.queryOptions({ id: resolvedTemplateId ?? 0 }),
    enabled: Boolean(resolvedTemplateId)
  })
  const versions = useQuery({
    ...trpc.contract.template.versions.queryOptions({ templateId: resolvedTemplateId ?? 0 }),
    enabled: Boolean(resolvedTemplateId && !isVersionView)
  })
  const saveDraft = useMutation(trpc.contract.template.saveDraft.mutationOptions())
  const draftVersion = versions.data?.find((item) => item.status === 'draft')
  const publishedVersion = versions.data?.find((item) => item.status === 'published')
  const contentVersionId = versionId ?? draftVersion?.id ?? publishedVersion?.id
  const contentKey = `${templateKey}-${contentVersionId ?? 'empty'}`
  const contentVersionDetail = useQuery({
    ...trpc.contract.template.versionDetail.queryOptions({ id: contentVersionId ?? 0 }),
    enabled: Boolean(contentVersionId)
  })
  const contentReady = loadedContentKey === contentKey
  const title = resolvedTemplateId ? detail.data?.name || '编辑合同模板' : '新建合同模板'
  const versionStatus = selectedVersion.data ? CONTRACT_TEMPLATE_VERSION_STATUS[selectedVersion.data.status] : undefined

  useEffect(() => {
    if (resolvedTemplateId && !detail.data) {
      return
    }
    if (hydratedTemplateKey.current === templateKey) {
      return
    }
    form.setFieldsValue(
      resolvedTemplateId
        ? {
            description: detail.data?.description ?? undefined,
            isEnabled: detail.data?.isEnabled ?? true,
            name: detail.data?.name ?? '',
            type: detail.data?.type ?? 'lease'
          }
        : NEW_TEMPLATE_INITIAL_VALUES
    )
    hydratedTemplateKey.current = templateKey
  }, [detail.data, form, resolvedTemplateId, templateKey])

  useEffect(() => {
    if (loadedContentKey === contentKey) {
      return
    }
    if (!resolvedTemplateId && !versionId) {
      setContent(NEW_TEMPLATE_CONTENT)
      setLoadedContentKey(contentKey)
      return
    }
    if (isVersionView ? !selectedVersion.isSuccess : !versions.isSuccess) {
      return
    }
    if (!contentVersionId) {
      setContent(EMPTY_CONTENT)
      setLoadedContentKey(contentKey)
      return
    }
    if (!contentVersionDetail.isSuccess) {
      return
    }
    setContent(contentVersionDetail.data.content as ContractLexicalContent)
    setLoadedContentKey(contentKey)
  }, [
    contentKey,
    contentVersionId,
    contentVersionDetail.data,
    contentVersionDetail.isSuccess,
    isVersionView,
    loadedContentKey,
    resolvedTemplateId,
    selectedVersion.isSuccess,
    versionId,
    versions.isSuccess
  ])

  const goBack = () => {
    void navigate({ to: '/contracts/templates', search: returnSearch })
  }

  const save = async (values: FormValues, publish = false) => {
    if (isReadOnly || !contentReady || saveDraft.isPending) {
      return
    }
    try {
      await saveDraft.mutateAsync({
        id: resolvedTemplateId,
        publish,
        template: {
          code: detail.data?.code ?? generatedCode,
          description: values.description?.trim() || null,
          isEnabled: values.isEnabled,
          name: values.name.trim(),
          type: values.type
        },
        changeNote: null,
        content,
        printSettings: null
      })
      void message.success(publish ? '合同模板已保存并发布' : resolvedTemplateId ? '合同模板已更新' : '合同模板已创建')
      await Promise.all([
        queryClient.invalidateQueries(trpc.contract.template.list.queryFilter()),
        queryClient.invalidateQueries(trpc.contract.template.options.queryFilter())
      ])
      goBack()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同模板保存失败'))
    }
  }

  const loading = Boolean(templateId || versionId) && (!detail.data || !contentReady)
  const detailError = resolvedTemplateId && detail.isError ? getErrorMessage(detail.error, '合同模板加载失败') : null
  const contentError = isVersionView
    ? selectedVersion.isError
      ? getErrorMessage(selectedVersion.error, '合同版本加载失败')
      : contentVersionDetail.isError
        ? getErrorMessage(contentVersionDetail.error, '合同正文加载失败')
        : null
    : templateId
      ? versions.isError
        ? getErrorMessage(versions.error, '合同正文加载失败')
        : contentVersionDetail.isError
          ? getErrorMessage(contentVersionDetail.error, '合同正文加载失败')
          : null
      : null
  const contentVersionLabel = isVersionView
    ? selectedVersion.data
      ? `v${selectedVersion.data.version} · ${versionStatus?.label ?? '历史版本'}`
      : '历史版本'
    : !contentReady
      ? '正在加载…'
      : draftVersion
        ? `草稿 v${draftVersion.version}`
        : '保存后创建草稿'

  return (
    <div className="bg-page flex h-[100dvh] min-h-0 flex-col overflow-hidden">
      <header className="border-border bg-surface sticky top-0 z-30 min-h-[3.625rem] shrink-0 border-b">
        <div className="flex h-full flex-wrap items-center justify-between gap-3 px-3 py-2 sm:px-4 lg:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="text-accent flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-soft)] sm:size-9">
                <FilePenLine aria-hidden size={16} />
              </span>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                  <Typography.Title
                    className="m-0! max-w-[10rem] min-w-0 truncate text-base! leading-5! sm:max-w-[28rem]"
                    level={3}
                  >
                    {title}
                  </Typography.Title>
                  <Tag
                    className="m-0! shrink-0 text-xs"
                    color={isReadOnly ? (versionStatus?.color ?? 'default') : 'processing'}
                  >
                    {isReadOnly ? (versionStatus ? `只读 · ${versionStatus.label}` : '只读查看') : '编辑草稿'}
                  </Tag>
                </div>
                <Typography.Text className="block truncate text-xs! leading-4! whitespace-nowrap" type="secondary">
                  {resolvedTemplateId
                    ? `模板编号 · ${detail.data?.code ?? '加载中…'}`
                    : '新建合同模板 · 保存后生成编号'}
                </Typography.Text>
              </div>
            </div>
          </div>
          {!isReadOnly ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button className="h-8! px-3!" onClick={goBack}>
                取消
              </Button>
              <Button
                className="h-8! px-3!"
                disabled={
                  saveDraft.isPending || loading || Boolean(contentError) || Boolean(detailError) || !contentReady
                }
                form="contract-template-form"
                htmlType="submit"
                icon={<Save aria-hidden size={15} />}
                loading={saveDraft.isPending}
              >
                保存模板
              </Button>
              <Authorized permissions={['contract.template.publish']}>
                <Button
                  className="h-8! px-3!"
                  disabled={
                    saveDraft.isPending || loading || Boolean(contentError) || Boolean(detailError) || !contentReady
                  }
                  loading={saveDraft.isPending}
                  type="primary"
                  onClick={() => {
                    void form
                      .validateFields()
                      .then((values) => save(values, true))
                      .catch(() => undefined)
                  }}
                >
                  保存模板并发布
                </Button>
              </Authorized>
            </div>
          ) : null}
        </div>
      </header>

      <main className="flex min-h-0 w-full flex-1 flex-col overflow-hidden px-3 py-3 sm:px-4 sm:py-4 lg:px-4 2xl:px-8">
        {detailError ? <Alert className="mb-3" showIcon title={detailError} type="error" /> : null}
        {contentError ? <Alert className="mb-3" showIcon title={contentError} type="error" /> : null}
        <Form<FormValues>
          id="contract-template-form"
          className="flex min-h-0 flex-1 flex-col [&_.ant-form-item]:mb-3 [&_.ant-form-item-label]:pb-1"
          form={form}
          layout="vertical"
          onFinish={(values) => save(values)}
          scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
        >
          <div className="grid min-h-0 flex-1 items-stretch gap-3 max-xl:grid-rows-[auto_minmax(0,1fr)] xl:grid-cols-[clamp(17rem,18vw,23rem)_minmax(0,1fr)] xl:gap-4">
            <section className="border-border bg-surface self-start rounded-xl border p-4 shadow-xs sm:p-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="bg-surface-muted text-content-muted flex size-7 items-center justify-center rounded-md">
                    <FilePenLine aria-hidden size={14} />
                  </span>
                  <div>
                    <Typography.Text className="block text-sm" strong>
                      模板信息
                    </Typography.Text>
                    <Typography.Text className="text-xs" type="secondary">
                      基础属性
                    </Typography.Text>
                  </div>
                </div>
                {resolvedTemplateId ? (
                  <Tag className="m-0!" color={detail.data?.isEnabled ? 'success' : 'default'}>
                    {detail.data?.isEnabled ? '启用' : '停用'}
                  </Tag>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-x-3 max-xl:grid-cols-3 xl:grid-cols-1">
                <Form.Item
                  label="模板名称"
                  name="name"
                  rules={[{ message: '请输入模板名称', required: true, whitespace: true }]}
                >
                  <Input disabled={isReadOnly} maxLength={255} placeholder="如：企业注册地址租赁合同" />
                </Form.Item>
                <Form.Item
                  extra={resolvedTemplateId ? '创建后不可修改' : undefined}
                  label="合同类型"
                  name="type"
                  rules={[{ message: '请选择合同类型', required: true }]}
                >
                  <Select
                    disabled={isReadOnly || Boolean(resolvedTemplateId)}
                    options={Object.entries(CONTRACT_TEMPLATE_TYPES).map(([value, item]) => ({
                      label: item.label,
                      value
                    }))}
                    placeholder="请选择合同类型"
                  />
                </Form.Item>
                <Form.Item className="!mb-0 xl:!mb-3" label="启用状态" name="isEnabled" valuePropName="checked">
                  <Switch checkedChildren="启用" disabled={isReadOnly} unCheckedChildren="停用" />
                </Form.Item>
              </div>
              <Form.Item label="模板说明" name="description">
                <Input.TextArea
                  autoSize={{ maxRows: 3, minRows: 2 }}
                  disabled={isReadOnly}
                  maxLength={5000}
                  placeholder="适用业务、签署场景或注意事项"
                  showCount
                />
              </Form.Item>
            </section>

            <section className="border-border bg-surface flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border shadow-xs">
              <div className="border-border flex shrink-0 flex-nowrap items-center justify-between gap-2 border-b px-3 py-2 sm:gap-3 sm:px-5 sm:py-3">
                <div className="flex items-center gap-2">
                  <span className="bg-accent size-2 rounded-full shadow-[0_0_0_0.25rem_var(--accent-soft)]" />
                  <div className="flex min-w-0 gap-2">
                    <Typography.Text className="block text-sm" strong>
                      合同正文
                    </Typography.Text>
                    <Typography.Text className="text-xs" type="secondary">
                      {isReadOnly ? '只读查看' : '编辑区'}
                    </Typography.Text>
                  </div>
                </div>
                <Typography.Text className="truncate text-xs" type="secondary">
                  {contentVersionLabel}
                </Typography.Text>
              </div>
              <div className="flex min-h-0 flex-1 flex-col">
                {contentError ? (
                  <Alert
                    showIcon
                    title={
                      isReadOnly ? '合同正文暂时无法加载，请刷新后重试。' : '合同正文暂时无法加载，请重试后再保存。'
                    }
                    type="error"
                  />
                ) : contentReady ? (
                  <ContractLexicalEditor
                    key={`${templateKey}-${contentVersionId ?? 'new'}`}
                    enableVariables
                    initialValue={content}
                    onChange={!isReadOnly ? setContent : undefined}
                    readOnly={isReadOnly}
                  />
                ) : (
                  <div className="border-border bg-surface-muted flex min-h-0 flex-1 items-center justify-center rounded-lg border">
                    <Space orientation="vertical" size="small">
                      <Spin />
                      <Typography.Text type="secondary">正在加载合同正文…</Typography.Text>
                    </Space>
                  </div>
                )}
              </div>
              <div className="border-border bg-surface-muted text-content-soft flex shrink-0 flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-xs sm:px-5">
                {isReadOnly ? (
                  <span>历史版本内容只读，不能修改</span>
                ) : (
                  <>
                    <span>
                      输入{' '}
                      <kbd className="border-border-strong bg-surface text-content-muted rounded border px-1.5 py-0.5 font-mono text-[0.6875rem]">
                        /
                      </kbd>{' '}
                      打开指令菜单；输入{' '}
                      <kbd className="border-border-strong bg-surface rounded border px-1.5 py-0.5 font-mono">@</kbd>{' '}
                      插入动态字段
                    </span>
                    <span>支持标题、列表、链接、表格、颜色与对齐</span>
                  </>
                )}
              </div>
            </section>
          </div>
        </Form>
      </main>
    </div>
  )
}
